import { useAuth } from "@/contexts/AuthContext";
import { ManualLeaveRequestDialog } from "@/components/leave/ManualLeaveRequestDialog";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { CalendarDays, FileText, ThumbsDown, ThumbsUp } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import {
  apiGetLeaveRequests,
  apiApproveLeaveRequest,
  apiRejectLeaveRequest,
  type LeaveRequest,
} from "@/lib/api";

const STATUS_COLORS: Record<string, string> = {
  Approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  Pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  Rejected: "bg-red-500/10 text-red-600 border-red-500/20",
  Cancelled: "bg-gray-500/10 text-gray-600 border-gray-500/20",
  "In Review": "bg-blue-500/10 text-blue-600 border-blue-500/20",
};

export default function MobileTeamLeave() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showManualCreate, setShowManualCreate] = useState(false);
  const dateLocale = useDateLocale();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<LeaveRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  const { data: requests = [] } = useQuery({
    queryKey: ["mobile-team-leave-requests"],
    queryFn: () => apiGetLeaveRequests(),
    staleTime: 60_000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => apiApproveLeaveRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-team-leave-requests"] });
      toast.success(t("mobile.teamLeave.approved"));
      setSelected(null);
    },
    onError: () => toast.error(t("mobile.teamLeave.actionFailed")),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      apiRejectLeaveRequest(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-team-leave-requests"] });
      toast.success(t("mobile.teamLeave.rejected"));
      setSelected(null);
      setShowReject(false);
      setRejectReason("");
    },
    onError: () => toast.error(t("mobile.teamLeave.actionFailed")),
  });

  const { pending, others } = useMemo(() => {
    const pending = requests.filter((r) => r.status === "Pending" || r.status === "In Review");
    const others = requests.filter((r) => r.status !== "Pending" && r.status !== "In Review");
    return { pending, others };
  }, [requests]);

  function Row({ r }: { r: LeaveRequest }) {
    return (
      <button
        type="button"
        onClick={() => setSelected(r)}
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left"
      >
        <EmployeeAvatar
          avatarUrl={null}
          avatarColor={r.employeeAvatarColor}
          initials={r.employeeInitials}
          name={r.employeeName}
          size="md"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{r.employeeName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {r.leaveTypeLabel ?? r.leaveType} · {format(parseISO(r.startDate), "d MMM", { locale: dateLocale })}
            {" – "}
            {format(parseISO(r.endDate), "d MMM yyyy", { locale: dateLocale })}
          </p>
        </div>
        <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[r.status] ?? ""}`}>
          {r.status}
        </Badge>
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {(user?.role.mainRole === "hr" || user?.role.mainRole === "admin") && <div className="p-4"><Button onClick={() => setShowManualCreate(true)}>{t("leave.createLeaveForEmployee")}</Button></div>}
      <ManualLeaveRequestDialog open={showManualCreate} onOpenChange={open => { setShowManualCreate(open); if (!open) void qc.invalidateQueries({ queryKey: ["mobile-team-leave-requests"] }); }} />
      <div className="flex-1 overflow-y-auto pb-4">
        {requests.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <CalendarDays className="h-10 w-10 opacity-30" />
            <p className="text-sm">{t("mobile.teamLeave.noRequests")}</p>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="mb-2">
                <div className="px-4 pt-4 pb-1 flex items-center gap-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t("mobile.teamLeave.pending")}
                  </span>
                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-amber-500/15 text-amber-600 text-[11px] font-bold">
                    {pending.length}
                  </span>
                </div>
                <div className="bg-card border border-border/60 mx-4 rounded-xl overflow-hidden">
                  {pending.map((r) => (
                    <Row key={r.id} r={r} />
                  ))}
                </div>
              </div>
            )}
            {others.length > 0 && (
              <div className="mb-2">
                <div className="px-4 pt-4 pb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t("mobile.teamLeave.history")}
                  </span>
                </div>
                <div className="bg-card border border-border/60 mx-4 rounded-xl overflow-hidden">
                  {others.map((r) => (
                    <Row key={r.id} r={r} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
            setShowReject(false);
            setRejectReason("");
          }
        }}
      >
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center gap-3">
                  <EmployeeAvatar
                    avatarUrl={null}
                    avatarColor={selected.employeeAvatarColor}
                    initials={selected.employeeInitials}
                    name={selected.employeeName}
                    size="md"
                  />
                  <span>{selected.employeeName}</span>
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-3 mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("mobile.calendar.leaveType")}</span>
                  <span className="font-medium">{selected.leaveTypeLabel ?? selected.leaveType}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("mobile.calendar.status")}</span>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[selected.status] ?? ""}`}>
                    {selected.status}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("mobile.calendar.dates")}</span>
                  <span className="font-medium">
                    {format(parseISO(selected.startDate), "d MMM", { locale: dateLocale })}
                    {" – "}
                    {format(parseISO(selected.endDate), "d MMM yyyy", { locale: dateLocale })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("mobile.calendar.duration")}</span>
                  <span className="font-medium">{selected.workingDays} {t("mobile.timeoff.days")}</span>
                </div>
                {selected.notes && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.teamLeave.reason")}</p>
                    <p className="text-foreground/90">{selected.notes}</p>
                  </div>
                )}
                {selected.createdOnBehalf && <p className="text-xs text-muted-foreground">
                  {t("leave.createdByOn", { name: selected.createdByName ?? t("leave.hrAdmin"), date: format(parseISO(selected.createdAt), "P", { locale: dateLocale }) })}
                </p>}
                {selected.rejectionReason && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.calendar.rejectionReason")}</p>
                    <p className="text-foreground/90">{selected.rejectionReason}</p>
                  </div>
                )}
                {selected.documentUrl && (
                  <a
                    href={selected.documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-primary"
                  >
                    <FileText className="h-4 w-4" />
                    {t("mobile.calendar.viewDocument")}
                  </a>
                )}
              </div>

              {(selected.status === "Pending" || selected.status === "In Review") && (
                <div className="mt-5 space-y-2">
                  {showReject ? (
                    <>
                      <Textarea
                        placeholder={t("mobile.teamLeave.rejectionReasonPlaceholder")}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="min-h-[80px]"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 h-10"
                          onClick={() => setShowReject(false)}
                        >
                          {t("mobile.teamLeave.cancel")}
                        </Button>
                        <Button
                          className="flex-1 h-10 bg-destructive hover:bg-destructive/90"
                          disabled={!rejectReason.trim() || rejectMutation.isPending}
                          onClick={() => rejectMutation.mutate({ id: selected.id, reason: rejectReason })}
                        >
                          {t("mobile.teamLeave.confirmReject")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={approveMutation.isPending}
                        onClick={() => approveMutation.mutate(selected.id)}
                      >
                        <ThumbsUp className="h-4 w-4 mr-1.5" />
                        {t("mobile.tasks.approve")}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-destructive border-destructive/40 hover:bg-destructive/10"
                        disabled={rejectMutation.isPending}
                        onClick={() => setShowReject(true)}
                      >
                        <ThumbsDown className="h-4 w-4 mr-1.5" />
                        {t("mobile.tasks.reject")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
